import type { QueryKey } from '@tanstack/react-query'

import type { UUID } from '@/types/Id'
import type { Period } from '@/types/pursuit/Pursuit'
import type { StatusType } from '@/types/Status'

export const normalizeOrgIds = (args: {
  orgId?: UUID | ''
  orgIds?: UUID[]
}): UUID[] => {
  if (args.orgIds) return args.orgIds
  if (args.orgId) return [args.orgId]
  return []
}

export const stableOrgIdsKey = (orgIds: UUID[]): string =>
  [...new Set(orgIds)].toSorted().join(',')

interface PursuitsQueryKeyArgs {
  orgId: UUID | ''
}

interface OrganizationsQueryKeyArgs {
  orgIds: UUID[]
}

interface PursuitStagesQueryKeyArgs {
  orgId: UUID | ''
}

interface PursuitCountQueryKeyArgs {
  orgId: UUID | ''
  period: Period
}

interface PursuitActiveAssignedQueryKeyArgs {
  orgId: UUID | ''
}

interface PursuitActionsQueryKeyArgs {
  pursuitId: UUID | ''
}

interface PursuitFilesQueryKeyArgs {
  pursuitId: UUID | ''
}

interface PursuitFilesIndexStatusQueryKeyArgs {
  pursuitId: UUID | ''
}

interface PursuitQueryKeyArgs {
  pursuitId: UUID | ''
}

export const pursuitsQueryKeys = {
  all: () => ['pursuits'] as const,
  organization: (keyArgs: PursuitsQueryKeyArgs) =>
    ['pursuits', keyArgs.orgId] as const,
  organizationsOverviewAll: () => ['pursuits', 'overview'] as const,
  organizationsOverview: (keyArgs: OrganizationsQueryKeyArgs) =>
    [
      ...pursuitsQueryKeys.organizationsOverviewAll(),
      stableOrgIdsKey(keyArgs.orgIds),
    ] as const,
}

// Matches every pursuits *list* cache — ['pursuits', orgId] and
// ['pursuits', 'overview', ...] — while excluding ['pursuits', 'latest', ...]
// caches, which hold a single Pursuit rather than a paginated list.
export const isPursuitsListKey = (key: QueryKey): boolean =>
  Array.isArray(key) && key[0] === 'pursuits' && key[1] !== 'latest'

export const pursuitActiveAssignedQueryKeys = {
  all: () => ['pursuit-active-assigned'] as const,
  organization: (keyArgs: PursuitActiveAssignedQueryKeyArgs) =>
    ['pursuit-active-assigned', keyArgs.orgId] as const,
}

export const pursuitCountQueryKeys = {
  all: () => ['pursuit-count'] as const,
  organization: (keyArgs: PursuitCountQueryKeyArgs) =>
    ['pursuit-count', keyArgs.orgId, keyArgs.period] as const,
}

export const pursuitQueryKeys = {
  all: () => ['pursuit'] as const,
  pursuit: (keyArgs: PursuitQueryKeyArgs) =>
    ['pursuit', keyArgs.pursuitId] as const,
}

export const pursuitActionsQueryKeys = {
  all: () => ['pursuit-actions'] as const,
  pursuit: (keyArgs: PursuitActionsQueryKeyArgs) =>
    ['pursuit-actions', keyArgs.pursuitId] as const,
}

export const pursuitFilesQueryKeys = {
  all: () => ['pursuit-files'] as const,
  pursuit: (keyArgs: PursuitFilesQueryKeyArgs) =>
    ['pursuit-files', keyArgs.pursuitId] as const,
}

interface PursuitFilesPaginatedQueryKeyArgs {
  pursuitId: UUID | ''
  pageLimit: number
}

export const pursuitFilesPaginatedQueryKeys = {
  allPages: (keyArgs: PursuitFilesPaginatedQueryKeyArgs) =>
    [
      ...pursuitFilesQueryKeys.pursuit({ pursuitId: keyArgs.pursuitId }),
      'all-pages',
      keyArgs.pageLimit,
    ] as const,
  singlePage: (keyArgs: PursuitFilesPaginatedQueryKeyArgs) =>
    [
      ...pursuitFilesQueryKeys.pursuit({ pursuitId: keyArgs.pursuitId }),
      'single-page',
      keyArgs.pageLimit,
    ] as const,
}

export const pursuitStagesQueryKeys = {
  all: () => ['pursuit-stages'] as const,
  organization: (keyArgs: PursuitStagesQueryKeyArgs) =>
    ['pursuit-stages', keyArgs.orgId] as const,
  organizationsOverviewAll: () => ['pursuit-stages', 'overview'] as const,
  organizationsOverview: (keyArgs: OrganizationsQueryKeyArgs) =>
    [
      ...pursuitStagesQueryKeys.organizationsOverviewAll(),
      stableOrgIdsKey(keyArgs.orgIds),
    ] as const,
}

export const pursuitFilesIndexStatusQueryKeys = {
  all: () => ['pursuit-files-index-status'] as const,
  live: (keyArgs: PursuitFilesIndexStatusQueryKeyArgs) =>
    ['pursuit-files-index-status', 'live', keyArgs.pursuitId] as const,
  pollBoost: (keyArgs: PursuitFilesIndexStatusQueryKeyArgs) =>
    ['pursuit-files-index-status', 'poll-boost', keyArgs.pursuitId] as const,
}

interface PursuitsIndexingStatusQueryKeyArgs {
  orgId: UUID | ''
  pursuitIds: UUID[]
}

export const pursuitsIndexingStatusQueryKeys = {
  all: () => ['pursuits-indexing-status'] as const,
  batch: (keyArgs: PursuitsIndexingStatusQueryKeyArgs) =>
    [
      'pursuits-indexing-status',
      keyArgs.orgId,
      [...keyArgs.pursuitIds].toSorted().join(','),
    ] as const,
}

// region: wiki

interface PursuitWikisQueryKeyArgs {
  pursuitId: UUID | ''
}

export const pursuitWikisQueryKeys = {
  all: ['pursuit-wikis'] as const,
  pursuit: (keyArgs: PursuitWikisQueryKeyArgs) =>
    [...pursuitWikisQueryKeys.all, keyArgs.pursuitId] as const,
}

interface PursuitWikiJobStatusQueryKeyArgs {
  pursuitId: UUID | ''
}

export interface WikiJobStatusResponse {
  status: StatusType
  job_type: 'generate-wiki'
}

export const pursuitWikiJobStatusQueryKeys = {
  all: ['pursuit-wiki-job-status'] as const,
  pursuit: (keyArgs: PursuitWikiJobStatusQueryKeyArgs) =>
    [...pursuitWikiJobStatusQueryKeys.all, keyArgs.pursuitId] as const,
}

// endregion: wiki

// region: stage due dates

interface PursuitStageDueDatesQueryKeyArgs {
  pursuitId: UUID | ''
}

export const pursuitStageDueDatesQueryKeys = {
  all: () => ['pursuit-stage-due-dates'] as const,
  pursuit: (keyArgs: PursuitStageDueDatesQueryKeyArgs) =>
    ['pursuit-stage-due-dates', keyArgs.pursuitId] as const,
}

// endregion: stage due dates

// region: dashboard analytics

interface StageVelocityQueryKeyArgs {
  orgId: UUID | ''
  daysLookback?: number
}

interface StageVelocityOrganizationsQueryKeyArgs {
  orgIds: UUID[]
  daysLookback?: number
}

interface StageConversionQueryKeyArgs {
  orgId: UUID | ''
  daysLookback?: number
}

interface StageConversionOrganizationsQueryKeyArgs {
  orgIds: UUID[]
  daysLookback?: number
}

interface RecentActivityQueryKeyArgs {
  orgId: UUID | ''
  limit?: number
  daysLookback?: number
  pursuitIds?: UUID[]
}

interface RecentActivityOrganizationsQueryKeyArgs {
  orgIds: UUID[]
  limit?: number
  daysLookback?: number
  pursuitIds?: UUID[]
}

export const stageVelocityQueryKeys = {
  all: () => ['stage-velocity'] as const,
  organization: (keyArgs: StageVelocityQueryKeyArgs) =>
    ['stage-velocity', keyArgs.orgId, keyArgs.daysLookback ?? 'all'] as const,
  organizationsOverview: (keyArgs: StageVelocityOrganizationsQueryKeyArgs) =>
    [
      'stage-velocity',
      'overview',
      stableOrgIdsKey(keyArgs.orgIds),
      keyArgs.daysLookback ?? 'all',
    ] as const,
}

export const stageConversionQueryKeys = {
  all: () => ['stage-conversion'] as const,
  organization: (keyArgs: StageConversionQueryKeyArgs) =>
    ['stage-conversion', keyArgs.orgId, keyArgs.daysLookback ?? 'all'] as const,
  organizationsOverview: (keyArgs: StageConversionOrganizationsQueryKeyArgs) =>
    [
      'stage-conversion',
      'overview',
      stableOrgIdsKey(keyArgs.orgIds),
      keyArgs.daysLookback ?? 'all',
    ] as const,
}

export const recentActivityQueryKeys = {
  all: () => ['recent-activity'] as const,
  organization: (keyArgs: RecentActivityQueryKeyArgs) =>
    [
      'recent-activity',
      keyArgs.orgId,
      keyArgs.limit ?? 10,
      keyArgs.daysLookback ?? 'all',
      [...(keyArgs.pursuitIds ?? [])].toSorted().join(','),
    ] as const,
  organizationsOverview: (keyArgs: RecentActivityOrganizationsQueryKeyArgs) =>
    [
      'recent-activity',
      'overview',
      stableOrgIdsKey(keyArgs.orgIds),
      keyArgs.limit ?? 10,
      keyArgs.daysLookback ?? 'all',
      [...(keyArgs.pursuitIds ?? [])].toSorted().join(','),
    ] as const,
}

interface WinLossQueryKeyArgs {
  orgId: UUID | ''
  daysLookback?: number
}

interface WinLossOrganizationsQueryKeyArgs {
  orgIds: UUID[]
  daysLookback?: number
}

export const winLossQueryKeys = {
  all: () => ['win-loss'] as const,
  organization: (keyArgs: WinLossQueryKeyArgs) =>
    ['win-loss', keyArgs.orgId, keyArgs.daysLookback ?? 'all'] as const,
  organizationsOverview: (keyArgs: WinLossOrganizationsQueryKeyArgs) =>
    [
      'win-loss',
      'overview',
      stableOrgIdsKey(keyArgs.orgIds),
      keyArgs.daysLookback ?? 'all',
    ] as const,
}

// endregion: dashboard analytics
